// Generic approval queue for the approval steps (Controller / Department /
// Director / Oversight / Export Control). Read-only on invoice data except the per-line-item GL
// code. Approve / Deny act per-invoice (green ✓ / red ✗ row buttons + the
// detail modal's action bar) and in bulk over checked rows ("<Approve> Selected"
// / "Deny Selected", app.md §8/§9). Only the BULK buttons get the "Are you sure?"
// warning; row/modal approve submits immediately, and row/modal deny opens just
// the required-comment dialog.
// Rows the API would refuse (canDecide, lib/identity.ts) are view-only: checkbox
// and Approve/Deny disabled with the reason in the tooltip.
// Hold (app.md §9): the row's lock button and the modal's Hold button — one
// shared `HoldControl` — stop the invoice where it is: a hold row with a
// required Category. Held rows render dimmed and can't be selected or decided
// until the hold is explicitly released (filled lock or Release Hold); their
// fields stay editable (owner, 2026-10-03 — D-282). The API enforces it too: a
// decision on a held invoice is refused with `Blocked by an active hold`.
//
// PORT NOTE: a decision is synchronous now. It either applies — the response
// carries the new status — or it is refused and writes nothing, with the reason
// in the error. So the "submitting…" rows, the re-poll schedule, the give-up
// warning and the rejected-decision audit rows are all gone; after a decision the
// queue simply reloads. Bulk is one request whose per-invoice results say what
// applied and why not, which is reported alongside the client-side pre-filter.
import { DECISION_SOURCE, type DecisionSource } from '@ap/shared'
import { Fragment, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  Button,
  Subtitle2,
  Caption1,
  Dropdown,
  Option,
  Toast,
  ToastBody,
  ToastTitle,
  makeStyles,
  tokens,
  useToastController,
} from '@fluentui/react-components'
import {
  CheckmarkRegular,
  ChevronDoubleDownRegular,
  ChevronDoubleUpRegular,
  DismissRegular,
} from '@fluentui/react-icons'
import type { Invoice } from '../lib/types'
import type { FiscalPick } from '../api/types'
import type { DecisionOutcome as DecisionOutcomeValue, ReasonCategory } from '../lib/constants'
import {
  DENY_CATEGORIES,
  DecisionOutcome,
  DecisionStep,
  MANUAL_ENTRY_CATEGORIES,
  TOASTER_ID,
} from '../lib/constants'
import { createDecision, createDecisionBatch } from '../lib/data'
import { actionA11y, heldReason, heldSelectionReason, useActiveHolds } from '../lib/holds'
import { ActionDivider } from '../components/ActionDivider'
import { HoldControl } from '../components/HoldControl'
import { errorMessage } from '../api/errors'
import { canDecide, filterDecidable, type Session } from '../lib/identity'
import type { RefreshSignal } from '../lib/refresh'
import { useInactiveAccountReasons, useQueue, useSelection } from '../lib/hooks'
import { codingGateReason, glCodeEditable } from '../lib/required'
import { DENY_BODY, bulkApproveBody, bulkDenyBody, heldNote, resolveDecisionBatch, skippedNote } from '../lib/decisions'
import { InvoiceQueue } from '../components/InvoiceQueue'
import { NeutralPill } from '../components/Badges'
import { InvoiceDetailModal } from '../components/InvoiceDetailModal'
import { DecisionDialog } from '../components/DecisionDialog'
import { applyFilters, type InvoiceFilter } from '../lib/filters'
import { formatCurrency } from '../lib/format'
import { useDecisionButtonStyles, useQueueToolbarButtonStyles } from '../lib/theme'
import { Loading, EmptyQueue, ErrorBanner } from '../components/States'
import { ScreenIntro } from '../components/ScreenIntro'
import { categoryPickableAt } from '../lib/required'

const useStyles = makeStyles({
  // NO `actionBar` / `count` (owner, 2026-09-15). The bulk buttons ride in the
  // queue's own toolbar beside Expand all now, so the strip they sat in — whose
  // left half had been empty by design since the count moved into the button on
  // 2026-09-14 — is gone, and `count` went with the count.
  spacer: { flex: 1 },
  // The coding gate's own caption, beside the bulk Approve it disables. Amber
  // rather than red: nothing is wrong, there is work left to do.
  uncoded: { color: tokens.colorPaletteDarkOrangeForeground1, maxWidth: '520px', textAlign: 'right' },
  // Per-company groups (Export Control File Generation — app.md §9 step 9).
  groups: { display: 'flex', flexDirection: 'column', gap: '28px' },
  // A gap below the header so the green action button never sits flush against
  // the first invoice card under it (owner, 2026-08-31).
  // Its buttons keep one line, as the queue toolbar's do (QA C2-05).
  groupHead: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    marginBottom: '12px',
    '& button': { whiteSpace: 'nowrap', flexShrink: 0 },
  },
  // Export Control's fiscal year and period pickers, left of the group's Export
  // button (owner, 2026-09-23, D-137 and D-138). A Fluent Dropdown will not
  // shrink below ~250px unless its `minWidth` is overridden (app/README.md).
  // ONE LINE, WHATEVER THE FONT (owner, 2026-10-04, D-292): the 150px year box
  // leaves 102px for its text, and "Fiscal Year 2026" measures 107px in the
  // Mac system font, so it wrapped onto two lines. The label never wraps
  // (`fiscalButton`) and `max-content` lets the box grow the few pixels it
  // needs instead of breaking — the width stays 150/170 wherever it fits.
  fiscalYear: { minWidth: 'max-content', width: '150px' },
  fiscalPeriod: { minWidth: 'max-content', width: '170px' },
  fiscalButton: { whiteSpace: 'nowrap' },
  // YEAR, PERIOD AND EXPORT AS ONE UNIT (QA 2026-10-04, C2-01): a flex box of
  // its own that does not wrap inside and does not shrink, so the header's
  // wrap moves all three to the next line together or leaves all three put.
  exportUnit: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'nowrap', flexShrink: 0 },
  // Export Control's orange section rule, repeated between company groups
  // (user preference 2026-07-12; grouped mode is Export-Control-only today).
  groupRule: { height: '3px', backgroundColor: '#E08A2B', borderRadius: '2px' },
})

interface ApprovalConfig {
  /** The page's one-line intro. Absent when the host screen renders its own
      (Export Control). The page's name is its header tab, not a title here. */
  subtitle?: string
  queueStatus: string
  step: DecisionStep
  approveLabel: string
  approveDisabled?: boolean
  approveDisabledReason?: string
  showPdf: boolean
  /** Unlock the per-line-item GL controls in the detail modal. Each screen
      passes the ones its step holds — the API checks the editor's authority for
      the step regardless and writes the correction row. `editAccounts` is "What
      is it?"; `editDepartments` is "Who is it for?". */
  editAccounts?: boolean
  editDepartments?: boolean
  /** REFUSE AN APPROVE while any active line item is missing either half of its
      GL code (owner, 2026-08-14 — `docs/decisions.md` D-9): "nothing should
      get through without a working GL code". Set on the approval tiers, which
      is exactly where the server's own gate applies (`codingGated` in
      `backend/src/http/routes/decisions.ts`). Export Control's Typed
      Into Point Click Care is gated too, by its own check below.

      The server is the enforcement; this is what stops the app OFFERING an action
      that would be refused. It reads `uncoded_line_item_count`, a derived field on
      the invoice header, so a fifty-row queue colours fifty buttons without
      reading fifty line-item collections.

      DENY IS NEVER GATED — a denial needs no coding — so an uncoded row stays
      selectable and bulk deny is untouched. */
  requireCodedLineItems?: boolean
  /** Group the queue by company, each group with its own Select all +
      "<Approve> Selected" / "Deny Selected" — matching the one-CSV-per-company
      export grain (Export Control File Generation — app.md §9 step 9). The
      page-level bulk bar is hidden; actions live in the groups. */
  groupByCompany?: boolean
  /** Export Control: an approve here is not a decision write but one export
      request, which builds the files, stamps the batch and writes every Export
      Control approval server-side. When present it REPLACES the decision write
      on an approve, and it reports how many invoices advanced. It throws with
      every problem listed when the set isn't exportable — nothing is written in
      that case. */
  approveVia?: (
    invoices: Invoice[],
    pick?: FiscalPick,
    /** Where the Export was clicked (D-331). */
    source?: DecisionSource,
  ) => Promise<{ appliedCount: number; /** Left out by the server as held (D-247). */ heldCount?: number }>
  /** Export Control ONLY (owner, 2026-09-23, D-137 and D-138): a Fiscal Year
      dropdown (defaulted to the current year) and a 1-12 Fiscal Period dropdown
      (no default) left of each company's Export button. Point Click Care's open
      period cannot be read off the invoice, so the controller picks it. The
      button stays disabled until a period is picked, and the pick is handed to
      `approveVia` as its second argument. */
  pickFiscalPeriod?: boolean
  /** Export Control ONLY (owner item 7, 2026-08-19): "remove the ability to
      approve, deny, and hold an invoice at this step. Also, remove the
      checkboxes on each entry, and remove the 'Select all' button. Replace
      the 'Approve & Export Selected' button with just a green 'Export'
      button." So this screen renders no checkboxes, no per-company Select
      all, no per-row or modal Approve/Deny — only a single per-company
      `config.approveLabel` button (green, `btn.approve` — that styling
      already exists, D-10) that acts on the WHOLE group, not a selection.

      HOLD CAME BACK (owner, 2026-09-30, D-247, reversing D-46): the row's
      lock beside Open and a labelled Hold in the modal, now left of the
      divider and Typed Into Point Click Care (D-311). A held invoice is left out of the group's Export — here and
      on the server — and stays in the queue until released. Deny stays
      refused on the server (D-46).

      Also skips the CLIENT-SIDE uncoded-lines pre-check that blocks opening
      the bulk-approve dialog on the three approval tiers: with no selection
      to narrow, the only way to name a bad line is to attempt the export and
      let the builder's own validation refuse it — which it already does,
      one line at a time (`ExportControlScreen.tsx`'s `validation_failed`
      handling). */
  exportOnly?: boolean
}

type Pending = {
  /** `manualEntry` is Export Control's Typed Into Point Click Care
      (D-311) — always one invoice, never bulk, and never a denial. */
  kind: 'approve' | 'deny' | 'manualEntry'
  ids: string[]
  bulk: boolean
  /** Where the click was, stored on the decision (D-331). */
  source: DecisionSource
} | null

/** The choices in Export Control's fiscal-period dropdown (D-137). */
const FISCAL_PERIODS = Array.from({ length: 12 }, (_, i) => i + 1)

/** The choices in Export Control's fiscal-year dropdown (D-138): last year, this
    year and next year, so a period that straddles New Year can still be picked.
    The current year is the default. */
function fiscalYearChoices(current: number): number[] {
  return [current - 1, current, current + 1]
}


interface Props {
  session: Session
  config: ApprovalConfig
  /** The shell's refresh signal (D-160): the Refresh and Pipeline buttons and the 30-second timer. */
  refresh?: RefreshSignal
  /** Page-wide filter (header Filter button; also drives the timeline badges). */
  filter: InvoiceFilter
  /** Called when acted rows leave the queue, so the timeline badges refresh too. */
  onQueuesChanged?: () => void
}

export function ApprovalScreen({ session, config, refresh, filter, onQueuesChanged }: Props) {
  const s = useStyles()
  const btn = useDecisionButtonStyles()
  const toolbarBtn = useQueueToolbarButtonStyles()
  const user = session.user
  // Declared ahead of `useQueue`: the queue is PAUSED while any of these is
  // open or in flight, so a background refresh never moves the gallery under a
  // modal or dialog; it catches up silently on close (D-160).
  const [openInvoice, setOpenInvoice] = useState<Invoice | null>(null)
  const [pending, setPending] = useState<Pending>(null)
  const [holdDialogOpen, setHoldDialogOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const { invoices, loading, error, reload } = useQueue([config.queueStatus], refresh, {
    paused: openInvoice !== null || pending !== null || holdDialogOpen || submitting,
  })
  const pageFiltered = useMemo(() => applyFilters(invoices, filter), [invoices, filter])
  // Owner ruling, 2026-08-14: "a user cannot see or act on any invoices
  // outside his role" — the queue shows only rows the caller can decide at
  // THIS screen's step, not every row their company access admits. Kept as
  // two steps, like the retired AP/AR Approval screen's original filter, so the empty
  // state below can tell "the page filter narrowed it to nothing" apart from
  // "none of these are yours to decide".
  const filtered = useMemo(
    () => filterDecidable(session, config.step, pageFiltered),
    [pageFiltered, session, config.step],
  )

  // Per-company groups (groupByCompany screens), alphabetical with unknown
  // company last; each keeps the queue's oldest-first order within it.
  const companyGroups = useMemo(() => {
    if (!config.groupByCompany) return null
    const byName = new Map<string, Invoice[]>()
    for (const inv of filtered) {
      const name = inv.company_name ?? '—'
      const group = byName.get(name)
      if (group) group.push(inv)
      else byName.set(name, [inv])
    }
    return [...byName.entries()]
      .sort(([a], [b]) => (a === '—' ? 1 : b === '—' ? -1 : a.localeCompare(b)))
      .map(([name, rows]) => ({ name, invoices: rows }))
  }, [config.groupByCompany, filtered])

  // Mirror the API's authorization gate: rows it would refuse are view-only —
  // not selectable, Approve/Deny disabled.
  const decidableIds = useMemo(
    () => new Set(filtered.filter((i) => canDecide(session, config.step, i)).map((i) => i.id)),
    [filtered, session, config.step],
  )

  // Expand all / Collapse all per COMPANY GROUP, lifted out of `InvoiceQueue`
  // so the button can sit inline beside the group's Export button (owner,
  // 2026-08-28). One record rather than a hook per group — groups are a map.
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})

  const byId = useMemo(() => new Map(invoices.map((i) => [i.id, i])), [invoices])
  // THE ITEM 9 FIX. `openInvoice` is a snapshot taken on row click; a GL edit
  // inside the modal reloads the QUEUE (and so `byId`) but never re-points
  // `openInvoice` itself, so its Approve gate — computed off
  // `uncoded_line_item_count` — kept reading the pre-edit value until the
  // modal was closed and reopened (the owner's exact workaround). Re-derive
  // the live invoice from `byId` on every render instead, and use IT for
  // everything the action bar and modal decide from. Falling back to
  // `openInvoice` when the id has left `byId` matters: right after an approve
  // the row leaves the queue entirely, and the modal must not blank out
  // mid-close. `openInvoice` itself stays as only the identity of what is open.
  //
  // Declared up here (rather than beside `settle`/`submit` below) because the
  // coding gate's OTHER consumers — the bulk caption, the per-row buttons,
  // and `approveNow`'s own guard — all need to resolve through the same
  // `byId`/`openInvoiceLive` pair too (see `uncodedFor` next).
  //
  // ONLY THE MODAL'S OWN RELOADS MOVE IT (D-160). The gallery also refreshes on
  // the 30-second timer and the Pipeline button; the queue is paused while the
  // modal is open, so neither can hand the open modal a row somebody else just
  // changed. The catch-up runs when the modal closes.
  const openInvoiceLive = openInvoice ? (byId.get(openInvoice.id) ?? openInvoice) : null

  // THE CODING GATE, CLIENT SIDE (D-9). A row whose invoice still has a line
  // missing "What is it?" or "Who is it for?" cannot be APPROVED — the server
  // refuses it, so the app must not offer it. Selection is deliberately NOT
  // blocked: deny needs no coding, and taking these rows out of `useSelection`
  // would break bulk deny along with bulk approve.
  //
  // ITEM 14(a) (owner item 14, 2026-08-17 — `docs/decisions.md`, the entry
  // superseding D-18 in part). A line coded Capital Equipment
  // (`190110-88-880`/`190110-88-885`) under the $5,000 floor has BOTH halves
  // set, so `uncoded_line_item_count` alone reads 0 for it and Approve used
  // to be OFFERED, then refused at the click (`422 capital_equipment_minimum`
  // — the reviewer's only signal before this change). Folding
  // `capital_equipment_under_floor_count` into the same count means every
  // consumer below it — the row button, the bulk caption, the pill, the modal
  // — disables Approve BEFORE the click, with no second code path to keep in
  // sync.
  //
  // THE MARKETING TERM (owner, 2026-09-15), folded in for exactly the same
  // reason: a Marketing-coded line with no Marketing Category has BOTH GL
  // halves set, so neither count above catches it, and Approve would be
  // offered and then refused at the click
  // (`409 line_item_without_marketing_category`).
  //
  // THE THREE ARE DISJOINT, which is what makes this sum a count of LINES —
  // the pill beside the button says "N line items need action", and a line missing
  // its Department AND its Marketing Category must not read as two. The
  // server's view enforces that: `missing_marketing_category_count` excludes a
  // line still missing either GL half. So such a line reports the Department
  // first and gains the Marketing clause once that half is answered — the same
  // staging "Account" → "Department" already has, and the same order the
  // server's own gate refuses in.
  const uncodedCount = (inv: Invoice | null | undefined): number =>
    config.requireCodedLineItems
      ? (inv?.uncoded_line_item_count ?? 0) +
        (inv?.capital_equipment_under_floor_count ?? 0) +
        (inv?.missing_marketing_category_count ?? 0)
      : 0
  const uncodedIds = useMemo(
    () => new Set(filtered.filter((i) => uncodedCount(i) > 0).map((i) => i.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- uncodedCount closes over config.requireCodedLineItems only
    [filtered, config.requireCodedLineItems],
  )
  /** Why this row cannot be approved, as the tooltip says it. Names what to
      do, never a column — a reviewer reads this, not a log.

      `where` picks the closing sentence, because the same shortfall reads
      differently depending on where the disabled button is: from a QUEUE row
      the fix starts by opening the invoice, but the modal's own Approve button
      is already inside it, where telling a reviewer to "open the invoice" is
      advice they cannot act on. Export Control's Typed Into
      Point Click Care makes the same distinction for its in-modal button. */
  // A line on an Inactive Account, named on the modal's Approve where this
  // step can still change the Account — Controller Approval (D-308). The
  // queue row cannot: it carries counts.
  const { reasons: strandedReasons, onLineItemsChange: onModalLines } = useInactiveAccountReasons(
    openInvoice?.id,
    glCodeEditable(config.step).account,
  )
  const uncodedReason = (
    inv: Invoice,
    where: 'queue' | 'modal' = 'queue',
    detail: readonly string[] = [],
  ): string | undefined => {
    if (!config.requireCodedLineItems) return undefined
    // THE SENTENCE IS `codingGateReason` (`lib/required.ts`) — one builder
    // for this button and Typed Into Point Click Care's, naming every cause that holds (QA
    // C1-01). The Capital Equipment clause, the "coding" vs "GL coding"
    // choice and the capitalize-whichever-leads rule all live there now.
    return codingGateReason(
      {
        missing: inv.uncoded_line_item_count ?? 0,
        capital: inv.capital_equipment_under_floor_count ?? 0,
        marketing: inv.missing_marketing_category_count ?? 0,
      },
      (glOnly) =>
        where === 'modal'
          ? glOnly
            ? 'Finish its GL coding before approving.'
            : 'Finish its coding before approving.'
          : glOnly
            ? 'Open the invoice and fix its GL coding before approving.'
            : 'Open the invoice and fix its coding before approving.',
      detail,
    )
  }
  // FAIL CLOSED (review fix): the single shared resolver every actual GATE —
  // as opposed to a merely informational caption — reads through. An id that
  // resolves to NOTHING (not in `byId`, not the open invoice) counts as
  // uncoded rather than coded, so a lookup miss blocks an approve instead of
  // silently letting it through.
  const uncodedFor = (id: string): number => {
    const inv = byId.get(id) ?? (openInvoiceLive?.id === id ? openInvoiceLive : undefined)
    return inv ? uncodedCount(inv) : 1
  }
  // The company approval tiers. Used twice: to word the not-authorized
  // message below, and to drop the read-only "Path to Point Click Care" row
  // from the modal (owner item 13, 2026-08-12) — those tiers neither set the
  // routing nor act on it. Export Control also runs through this screen and
  // keeps the row.
  const isCompanyTier =
    config.step === DecisionStep.Controller ||
    config.step === DecisionStep.Department ||
    config.step === DecisionStep.Director ||
    config.step === DecisionStep.Oversight

  // All three approval tiers answer per company, so Oversight belongs in the
  // first branch too. The case that put it there (2026-08-10, when oversight
  // became a per-company tier): hold oversight for company A and a director
  // seat for company B, and B's invoices load into the Oversight queue — they
  // are in your row-level-security company set — while `canDecide` refuses
  // them. The generic sentence would tell that person they do not hold the
  // role, which they do, just not for that company.
  //
  // D-124 (2026-09-15) made the Oversight seat org-wide, so that case no
  // longer arises for Oversight: an `All` seat covers every company the queue
  // can show. It stays in this branch anyway. The sentence is still the right
  // one if it ever renders — a company-scoped oversight row is refused by the
  // database, not by this screen — and the branch is also what a
  // department-head or director hits, which is unchanged.
  const notAuthorizedReason = isCompanyTier
    ? "Only an approver assigned to this invoice's own company can decide on it at this step. You can view it, but your decision would be refused."
    : "You don't hold the role that decides at this step. You can view this invoice, but your decision would be refused."

  // Active holds for the loaded queue, keyed by id. A held invoice is stopped:
  // dimmed, unselectable, Approve/Deny disabled. Its coding stays editable (D-282).
  const { holdFor, isHeld, heldIdsIn, setHoldFor } = useActiveHolds(invoices)
  // HELD BY THE ROW'S OWN FLAG TOO (owner, 2026-10-01, D-263): a row that says
  // held is dimmed, unselectable and, at Export Control, out of the pills and
  // the Export before its hold record arrives.
  const heldIds = useMemo(() => heldIdsIn(filtered), [filtered, heldIdsIn])

  const sel = useSelection(
    useMemo(() => [...decidableIds].filter((id) => !heldIds.has(id)), [decidableIds, heldIds]),
  )

  // How many SELECTED rows are blocked — what disables the bulk Approve and what
  // its caption reports. Locking bulk approve outright (rather than silently
  // approving the coded subset) is the owner's ask: "the approve button… should be
  // locked if any line item on that invoice has a missing value."
  const selectedUncoded = useMemo(
    () => [...sel.selected].filter((id) => uncodedFor(id) > 0).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- uncodedFor closes over byId/openInvoiceLive/config.requireCodedLineItems, all already tracked via their own deps
    [sel.selected, byId, openInvoiceLive],
  )
  // WHICH of the two causes is blocking, counted across the selection — the same
  // split `uncodedReason` already makes for a single row. `uncodedCount` folds
  // `capital_equipment_under_floor_count` into `uncoded_line_item_count` so that
  // one number can gate every surface (item 14(a)), and this caption used to word
  // that combined number as if only the missing-half case could produce it. A
  // selection blocked SOLELY by the Capital Equipment floor was therefore told a
  // line had no "full GL code" and to "finish its coding" — untrue of a line
  // whose halves are both set, and it sends the reviewer hunting for an empty
  // field that does not exist (QA walk, 2026-09-08). The gate itself was right;
  // only the explanation was wrong, and the row button, the modal lock icon and
  // the Department placeholder all named the floor correctly beside it.
  //
  // An invoice can carry both causes and is counted under both. The GATE stays
  // `selectedUncoded` — unchanged, still fail-closed on an id that resolves to
  // nothing — so this only ever changes words, never what is approvable.
  const selectedBlockedBy = useMemo(() => {
    let missing = 0
    let capital = 0
    let marketing = 0
    for (const id of sel.selected) {
      const inv = byId.get(id) ?? (openInvoiceLive?.id === id ? openInvoiceLive : undefined)
      // Fail closed, and with no cause to name: an unresolved id is reported as a
      // missing half, the clause whose remedy ("open it and finish the coding")
      // is right either way.
      if (!inv) {
        missing++
        continue
      }
      if (!config.requireCodedLineItems) continue
      if ((inv.uncoded_line_item_count ?? 0) > 0) missing++
      if ((inv.capital_equipment_under_floor_count ?? 0) > 0) capital++
      if ((inv.missing_marketing_category_count ?? 0) > 0) marketing++
    }
    return { missing, capital, marketing }
  }, [sel.selected, byId, openInvoiceLive, config.requireCodedLineItems])

  const bulkUncodedReason = (() => {
    if (selectedUncoded === 0) return undefined
    const { missing, capital, marketing } = selectedBlockedBy
    const parts: string[] = []
    if (missing > 0) {
      parts.push(
        missing === 1
          ? 'one selected invoice still has a line item without a full code'
          : `${missing} selected invoices still have line items without a full code`,
      )
    }
    if (capital > 0) {
      parts.push(
        capital === 1
          ? 'one selected invoice has a line item coded Capital Equipment but under the $5,000 minimum — pick a different “Account” for it'
          : `${capital} selected invoices have line items coded Capital Equipment but under the $5,000 minimum — pick a different “Account” for them`,
      )
    }
    if (marketing > 0) {
      parts.push(
        marketing === 1
          ? 'one selected invoice has a Marketing line item without a “Marketing Category”'
          : `${marketing} selected invoices have Marketing line items without a “Marketing Category”`,
      )
    }
    // Neither clause matched a resolvable cause — keep the old wording rather
    // than render an empty sentence beside a disabled button.
    if (parts.length === 0) {
      return selectedUncoded === 1
        ? 'One selected invoice still has a line item without a full code. Finish its coding, or clear it from the selection, before approving.'
        : `${selectedUncoded} selected invoices still have line items without a full code. Finish their coding, or clear them from the selection, before approving.`
    }
    // Same capitalize-whichever-leads treatment as `uncodedReason`, for the same
    // reason: the capital clause is written lower-case to follow ", and", and
    // leads on its own whenever the floor is the only failing term.
    const sentence = parts.join(', and ')
    const advice =
      selectedUncoded === 1
        ? 'Finish its coding, or clear it from the selection, before approving.'
        : 'Finish their coding, or clear them from the selection, before approving.'
    return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}. ${advice}`
  })()

  // Bumped when the detail modal changes a line item (the GL code picker), so
  // the expanded card behind it re-reads its lines. The queue's own reloads no
  // longer invalidate that cache (InvoiceQueue keys it on a row signature), and
  // a line item-only edit doesn't touch the invoice row.
  const [lineItemsNonce, setLineItemsNonce] = useState(0)
  /**
   * WHAT THE DIALOG SAYS, held separately from WHETHER IT IS OPEN.
   *
   * `pending` is nulled the moment a decision settles, and Fluent keeps the
   * surface mounted through its exit animation — so a dialog reading `pending`
   * directly flashed the DENY wording on its way out. A dialog that had just
   * diverted an invoice showed “Deny this invoice?” over a Deny button (found on
   * a walk of the running harness, 2026-08-31; it did the same on an approve,
   * where the closing frame read “Are you sure?” → “Deny this invoice?”).
   *
   * Set at OPEN time and never cleared, so the closing frame keeps saying what
   * the reviewer actually did. `open` still keys off `pending`, which is what
   * makes the dialog close at all.
   */
  const [dialogContent, setDialogContent] = useState<Pending>(null)
  function openPending(next: NonNullable<Pending>) {
    setDialogContent(next)
    setPending(next)
  }
  /** Export Control's fiscal period per company group (D-137). Starts empty on
      purpose — nothing is assumed — and a group's pick is cleared once its
      export lands, so a later batch for the same company must be picked again. */
  const [fiscalPeriodByGroup, setFiscalPeriodByGroup] = useState<Record<string, number>>({})
  /** The year per group (D-138). A group with no entry shows the current year —
      the owner's chosen default — so only a change away from it is stored. */
  const [fiscalYearByGroup, setFiscalYearByGroup] = useState<Record<string, number>>({})
  const currentYear = new Date().getFullYear()
  /**
   * NO CONFIRMATION ON EXPORT (owner, 2026-09-23, D-138): the controller picks a
   * period every time, which is deliberate enough. Without a dialog in the way, a
   * double-click could fire two exports before `submitting` re-renders the button
   * disabled; the second would be refused as already exported and put a false
   * error on screen. This ref closes that gap synchronously.
   */
  const exportInFlight = useRef(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const { dispatchToast } = useToastController(TOASTER_ID)

  /** Finish a submit: drop the dialogs, reload the queue and the badges. */
  function settle() {
    setPending(null)
    setOpenInvoice(null)
    sel.clear()
    // Silent (owner, 2026-09-28): a loud reload swaps the list for a
    // placeholder, the page shrinks, and the reviewer is thrown to the top.
    void reload({ silent: true })
    onQueuesChanged?.()
  }

  async function submit(
    requestedIds: string[],
    outcome: DecisionOutcomeValue,
    reason?: string,
    category?: string,
    exportGroup?: { company: string; pick: FiscalPick; heldCount: number },
    /** Where the click was (D-331): the bar, the row, or the modal. */
    source?: DecisionSource,
  ) {
    setSubmitting(true)
    setActionError(null)
    const approved = outcome === DecisionOutcome.Approved
    // Typed Into Point Click Care is neither an approval nor a
    // denial (D-311), so it has its own sentence.
    const manuallyEntered = outcome === DecisionOutcome.RequiresManualEntry
    // A toast is a SENTENCE, not a caption (owner, 2026-09-10). The count and
    // the outcome are composed together so the subject and the verb agree.
    const outcomeSentence = (count: number): string => {
      const subject = count === 1 ? 'The invoice was' : `${count} invoices were`
      if (approved) return `${subject} approved.`
      if (manuallyEntered) return `${subject} marked as manually entered into Point Click Care.`
      return `${subject} denied and routed to Denial Review.`
    }
    // Re-resolve the batch NOW, not when the dialog opened: the queue reloads
    // behind an open dialog, so a row may have left it, gone on hold, or moved
    // out of this user's authority since the selection was snapshotted. The API
    // would refuse those; filtering here keeps the confirmation honest about
    // what is actually being submitted (lib/decisions.ts).
    const batch = resolveDecisionBatch(
      requestedIds,
      (id) => byId.get(id),
      (inv) => canDecide(session, config.step, inv) && !isHeld(inv),
    )
    const acted = batch.eligible
    if (acted.length === 0) {
      setPending(null)
      setOpenInvoice(null)
      sel.clear()
      dispatchToast(
        <Toast>
          <ToastTitle>
            {batch.blocked.length === 0
              ? 'Those invoices were already processed, so nothing was submitted.'
              : 'Nothing was submitted, because those invoices are on hold or not yours to decide.'}
          </ToastTitle>
        </Toast>,
        { intent: 'info' },
      )
      setSubmitting(false)
      return
    }
    const skipped = skippedNote(batch)

    // Export Control's approve is its own request (see approveVia).
    if (approved && config.approveVia) {
      try {
        const { appliedCount, heldCount = 0 } = await config.approveVia(acted, exportGroup?.pick, source)
        // Held invoices the screen never sent, plus any the server left out
        // because a hold landed after this screen last read the holds (D-247).
        const held = heldNote((exportGroup?.heldCount ?? 0) + heldCount)
        const exportedCompany = exportGroup?.company
        if (exportedCompany !== undefined) {
          const without = (prev: Record<string, number>) => {
            const next = { ...prev }
            delete next[exportedCompany]
            return next
          }
          setFiscalPeriodByGroup(without)
          setFiscalYearByGroup(without)
        }
        settle()
        dispatchToast(
          <Toast>
            <ToastTitle>
              {appliedCount === 1
                ? 'The invoice was approved and exported.'
                : `${appliedCount} invoices were approved and exported.`}
            </ToastTitle>
            {(held || skipped) && <ToastBody>{[held, skipped].filter(Boolean).join(' ')}</ToastBody>}
          </Toast>,
          { intent: 'success' },
        )
      } catch (e) {
        setActionError(errorMessage(e))
      } finally {
        setSubmitting(false)
      }
      return
    }

    // One invoice: the single-decision endpoint, whose refusal reason is the
    // error message (`Blocked by an active hold`, `Out of order — record status
    // is …`, `Not authorized for this step`).
    if (acted.length === 1) {
      try {
        await createDecision({
          invoice_id: acted[0].id,
          step: config.step,
          decision: outcome,
          reason: reason || undefined,
          reason_category: (category as ReasonCategory) || undefined,
          row_version: acted[0].row_version,
          source,
        })
        settle()
        dispatchToast(
          <Toast>
            <ToastTitle>{outcomeSentence(1)}</ToastTitle>
          </Toast>,
          { intent: 'success' },
        )
      } catch (e) {
        setActionError(errorMessage(e))
      } finally {
        setSubmitting(false)
      }
      return
    }

    // Bulk: one request, each invoice its own transaction. It always resolves,
    // so the report names exactly what the API refused and why.
    try {
      const result = await createDecisionBatch({
        step: config.step,
        decision: outcome,
        reason: reason || undefined,
        reason_category: (category as ReasonCategory) || undefined,
        invoice_ids: acted.map((i) => i.id),
        source,
      })
      settle()
      const refusals = result.results.filter((r) => !r.applied)
      if (refusals.length > 0) {
        const named = refusals
          .map((r) => {
            const inv = byId.get(r.invoice_id)
            const label = inv?.external_invoice_id || inv?.vendor_name || r.invoice_id
            return `${label}: ${r.error?.message ?? 'refused'}`
          })
          .join('; ')
        setActionError(
          `${result.applied_count} of ${result.results.length} went through. Not applied — ${named}`,
        )
      }
      dispatchToast(
        <Toast>
          <ToastTitle>
            {outcomeSentence(result.applied_count)}
          </ToastTitle>
          {skipped && <ToastBody>{skipped}</ToastBody>}
        </Toast>,
        { intent: result.rejected_count > 0 ? 'warning' : 'success' },
      )
    } catch (e) {
      setActionError(errorMessage(e))
    } finally {
      setSubmitting(false)
    }
  }

  // Row/modal approve commits without a confirmation — the warning is bulk-only.
  // Both paths re-check the coding gate: the buttons are already disabled, and
  // this is the belt to that braces — a stale render must not submit an approve
  // the server will refuse.
  function approveNow(ids: string[], source: DecisionSource) {
    if (ids.length === 0 || config.approveDisabled) return
    // `uncodedFor`, NOT `uncodedIds` — that set is built from `filtered`,
    // which a page filter can narrow independently of the queue itself, so it
    // can disagree with `openInvoiceLive`'s own gate on the SAME invoice
    // (B12), and it silently reads as "coded" for any id it doesn't contain.
    // `uncodedFor` reads through the same `byId`/`openInvoiceLive` resolution
    // the button itself is gated on, and fails CLOSED on a lookup miss.
    if (ids.some((id) => uncodedFor(id) > 0)) return
    submit(ids, DecisionOutcome.Approved, undefined, undefined, undefined, source)
  }
  function openBulkApprove(ids: string[]) {
    if (ids.length === 0 || config.approveDisabled) return
    // Export Control (`exportOnly`) skips this pre-check on purpose: `ids`
    // here is the WHOLE company group, not a hand-picked selection, so there
    // is no "uncheck the bad one" move left to offer. Attempt the export and
    // let the builder's own validation refuse it, naming every offending
    // line — the only signal left once selection is gone (owner item 7,
    // 2026-08-19).
    if (!config.exportOnly && ids.some((id) => uncodedFor(id) > 0)) return
    openPending({ kind: 'approve', ids, bulk: true, source: DECISION_SOURCE.bulk_queue })
  }
  /**
   * Export Control's Export: straight to the request, no "Are you sure?" (owner,
   * 2026-09-23, D-138 — picking the period every time is confirmation enough).
   * `ids` is the WHOLE company group, so there is no uncoded pre-check here: the
   * builder's own validation names every offending line (owner item 7).
   */
  async function exportGroupNow(ids: string[], company: string, pick: FiscalPick | undefined, heldCount: number) {
    if (ids.length === 0 || config.approveDisabled || submitting) return
    // No export without a picked period (D-137) — the button is already
    // disabled; this stops a stale render from sending one anyway.
    if (config.pickFiscalPeriod && pick === undefined) return
    if (exportInFlight.current) return
    exportInFlight.current = true
    try {
      // The company group's Export is the queue's own bulk action (D-331).
      await submit(ids, DecisionOutcome.Approved, undefined, undefined, pick ? { company, pick, heldCount } : undefined, DECISION_SOURCE.bulk_queue)
    } finally {
      exportInFlight.current = false
    }
  }
  function openDeny(ids: string[], source: DecisionSource) {
    if (ids.length === 0) return
    openPending({ kind: 'deny', ids, bulk: source === DECISION_SOURCE.bulk_queue, source })
  }
  /**
   * Typed Into Point Click Care, from the Export Control modal
   * (owner, 2026-10-07 — D-311). ONE invoice — the one the controller has open
   * and has keyed in by hand — never a group. It ends the invoice, tagged as
   * manually entered, once the pop-up is submitted, and not before.
   *
   * CODING-GATED, as the Manual Entry step's own button was: it is the last
   * look before Point Click Care on a path that builds no CSV, so the server
   * refuses an uncoded line (`codingGated`) and the button is disabled for one.
   */
  function openManualEntry(id: string) {
    openPending({ kind: 'manualEntry', ids: [id], bulk: false, source: DECISION_SOURCE.modal })
  }

  const selectedIds = [...sel.selected]

  // THE ONE HOLD CONTROL (owner, 2026-09-25), on the row as an icon and in the
  // modal as a labelled button — at all four instances of this screen,
  // Export Control included since D-247.
  function holdControl(inv: Invoice, decidable: boolean, variant: 'button' | 'icon') {
    return (
      <HoldControl
        invoiceId={inv.id}
        hold={holdFor(inv)}
        decidable={decidable}
        notAuthorizedReason={notAuthorizedReason}
        variant={variant}
        disabled={submitting}
        onError={setActionError}
        onDialogOpenChange={setHoldDialogOpen}
        onChanged={(hold) => {
          setHoldFor(inv.id, hold)
          sel.clear()
          void reload({ silent: true })
          onQueuesChanged?.()
        }}
      />
    )
  }

  // One queue rendering shared by the grouped (per-company) and ungrouped
  // paths — only the row set, Select all wiring, and totals footer differ.
  function renderQueue(
    rows: Invoice[],
    opts: {
      allSelected: boolean
      onToggleAll: () => void
      /** Set only by the grouped path, whose header carries the button. */
      expandAll?: boolean
      /** Set only by the UNGROUPED path — the bulk buttons ride in the queue's
          own toolbar, beside Expand all (owner, 2026-09-15). The grouped path
          builds the same row per company in its group header instead. */
      toolbarActions?: ReactNode
    },
  ) {
    return (
      <InvoiceQueue
        toolbarActions={opts.toolbarActions}
        invoices={rows}
        screen={config.step}
        columns="amounts"
        // No checkboxes at all for Export Control (owner item 7, 2026-08-19)
        // — there is no selection to make any more; the group's own Export
        // button acts on the whole company. Every other tier is unaffected.
        selectable={!config.exportOnly}
        selectedIds={sel.selected}
        onToggle={sel.toggle}
        onToggleAll={opts.onToggleAll}
        allSelected={opts.allSelected}
        onOpen={setOpenInvoice}
        // Both halves, read-only, in the expanded card's line-items table —
        // never the composed Code column (owner item 7, 2026-08-19:
        // "Account should never appear anywhere in the Invoices tab" — the
        // composed column was labelled Account then). The card shows the
        // same halves the MODAL does (`editAccounts`/`editDepartments`,
        // narrowed per invoice by the route lock — D-305).
        codingDisplay="halves"
        // OWNER ITEM 22 (2026-08-31) — the same two pickers, live, in the
        // expanded card: "Can we please change these text values into
        // dropdown selectors, like we have for Account and Department in the
        // modal, so users can edit these values without needing to open the
        // modal?"
        //
        // THE SAME TWO FLAGS THE MODAL BELOW IS GIVEN, read straight off the
        // config — never a screen list of this screen's own. That is what
        // keeps the card from offering a control the server would refuse:
        // whatever an instance of this screen grants in the modal, it grants
        // here, and `InvoiceQueue` narrows each row by the route lock
        // (`glCodeEditable`, D-305) exactly as the modal does. A future
        // instance that grants neither gets a read-only card, with no second
        // place to remember to narrow.
        //
        // `InvoiceQueue` drops both for any row in `heldIds` on its own — a
        // hold blocks every GL write path.
        editAccounts={config.editAccounts}
        editDepartments={config.editDepartments}
        // THE ADDITIONAL DETAIL are not passed: `InvoiceQueue` reads them off
        // `screen` through `categoryPickableAt` (`lib/required.ts`, D-192).
        // An applied inline write changes `uncoded_line_item_count`, which is
        // what the coding-gate pill and the Approve button read. Reload the
        // rows so both re-derive. NOT a `lineItemsNonce` bump: the queue has
        // already re-read that card's own lines, and bumping the nonce would
        // dump and refetch every OTHER expanded card for nothing.
        onCodingChanged={() => void reload({ silent: true })}
        // The coding-gate pill (owner, 2026-08-17): only ever wired up when
        // this screen actually enforces D-9, and reading through the SAME
        // `uncodedCount`/`uncodedReason` helpers the Approve button and its
        // tooltip use, so the pill can never disagree with the button beside
        // it. Never pass `inv.uncoded_line_item_count` straight through.
        uncodedLinesFor={
          config.requireCodedLineItems
            ? (inv) => ({ count: uncodedCount(inv), tooltip: uncodedReason(inv) })
            : undefined
        }
        // Export Control's row carries the lock beside Open and nothing else
        // (owner, 2026-09-30, D-247: "the lock icon next to the Open button in
        // the gallery"); no per-row Approve or Deny there (owner item 7).
        rowActions={
          config.exportOnly
            ? (inv) => holdControl(inv, decidableIds.has(inv.id), 'icon')
            : (inv) => {
                const decidable = decidableIds.has(inv.id)
                const hold = holdFor(inv)
                const held = !!hold
                const heldTitle = hold ? heldReason(hold) : undefined
                // THE REASON MUST REACH THE ACCESSIBLE NAME. Both buttons are
                // icon-only, so they need an `aria-label` — but an `aria-label`
                // WINS over `title`, so the flat `'Approve'` / `'Deny'` these
                // carried left a screen-reader user with the label and none of
                // the reason the sighted hover gives (QA walk, 2026-08-30). Fold
                // the reason in, exactly as the Release button already does
                // (`EditableInvoiceForm.tsx`, `Release — ${blocked}`). A HOLD is
                // the exception: it is the description, not the name, so a held
                // row still reads "Approve" (`actionA11y`, QA C2-04).
                const otherApproveReason =
                  uncodedReason(inv) ??
                  (!decidable ? notAuthorizedReason : config.approveDisabled ? config.approveDisabledReason : config.approveLabel)
                const approveTitle = heldTitle ?? otherApproveReason
                const denyTitle = heldTitle ?? (decidable ? 'Deny' : notAuthorizedReason)
                return (
                  <>
                    {holdControl(inv, decidable, 'icon')}
                    <Button
                      size="small"
                      className={btn.deny}
                      icon={<DismissRegular />}
                      title={denyTitle}
                      {...actionA11y('Deny', { held: heldTitle, reason: denyTitle })}
                      disabled={submitting || !decidable || held}
                      onClick={() => openDeny([inv.id], DECISION_SOURCE.queue_row)}
                    />
                    <Button
                      size="small"
                      className={btn.approve}
                      icon={<CheckmarkRegular />}
                      title={approveTitle}
                      {...actionA11y(config.approveLabel, { held: heldTitle, reason: otherApproveReason })}
                      disabled={submitting || config.approveDisabled || !decidable || held || uncodedFor(inv.id) > 0}
                      onClick={() => approveNow([inv.id], DECISION_SOURCE.queue_row)}
                    />
                  </>
                )
              }
        }
        selectionDisabled={(inv) => {
          const hold = holdFor(inv)
          if (hold) return heldSelectionReason(hold)
          return decidableIds.has(inv.id) ? undefined : notAuthorizedReason
        }}
        dimmedIds={heldIds}
        heldIds={heldIds}
        lineItemsNonce={lineItemsNonce}
        expandAll={opts.expandAll}
      />
    )
  }

  return (
    <div>
      {config.subtitle && <ScreenIntro>{config.subtitle}</ScreenIntro>}

      <ErrorBanner message={error || actionError} />

      {loading ? (
        <Loading label="Loading invoices…" />
      ) : filtered.length === 0 ? (
        <EmptyQueue
          text={
            pageFiltered.length > 0
              ? 'Nothing here is assigned to you right now.'
              : invoices.length > 0
                ? 'No invoices match the current filters.'
                : undefined
          }
        />
      ) : companyGroups ? (
        <div className={s.groups}>
          {companyGroups.map((g, idx) => {
            const groupIds = g.invoices.map((i) => i.id)
            const selectableIds = groupIds.filter((id) => decidableIds.has(id) && !heldIds.has(id))
            const groupSelected = groupIds.filter((id) => sel.selected.has(id))
            const groupAllSelected =
              selectableIds.length > 0 && selectableIds.every((id) => sel.selected.has(id))
            const groupUncoded = groupSelected.filter((id) => uncodedIds.has(id)).length
            // Held invoices in the group: never sent, and counted in the
            // success message (D-247).
            const groupHeld = groupIds.filter((id) => heldIds.has(id)).length
            // WHAT THE PILLS COUNT (owner, 2026-09-30 — D-247): at Export
            // Control, only what the Export will send. A held invoice stays
            // listed below but is left out of both numbers.
            const counted = config.exportOnly ? g.invoices.filter((i) => !heldIds.has(i.id)) : g.invoices
            const groupPeriod = fiscalPeriodByGroup[g.name]
            const groupYear = fiscalYearByGroup[g.name] ?? currentYear
            const needsPeriod = !!config.pickFiscalPeriod && groupPeriod === undefined
            const groupPick: FiscalPick | undefined =
              groupPeriod === undefined ? undefined : { fiscalYear: groupYear, fiscalPeriod: groupPeriod }
            // Why the group's Export is disabled: its tooltip, and the part
            // of its name after the label (C2-03, D-157).
            const exportWhy = config.approveDisabled
              ? config.approveDisabledReason
              : selectableIds.length === 0
                ? 'Nothing in this company is exportable right now — every invoice is on hold or not yours to decide.'
                : needsPeriod
                  ? 'Pick a fiscal period first.'
                  : undefined
            return (
              <Fragment key={g.name}>
                {idx > 0 && <div className={s.groupRule} aria-hidden />}
                <section>
                <div className={s.groupHead}>
                  <Subtitle2>{g.name}</Subtitle2>
                  {/* Export Control (owner item 7, 2026-08-19): no selection
                      any more, so no "N of M selected" count — just the
                      company's invoice count. The COUNT and the TOTAL are the
                      whole of what the dropped per-group summary box said
                      (owner, 2026-08-28), as two gray pills on this line. */}
                  {/* WHAT THE GROUP HOLDS, on both branches (owner,
                      2026-09-14). The non-export arm read "3 of 12 selected",
                      which was the last "x selected" left on screen after the
                      count moved into the buttons themselves. */}
                  <NeutralPill>
                    {`${counted.length} invoice${counted.length === 1 ? '' : 's'}`}
                  </NeutralPill>
                  <NeutralPill title="Sum of these invoices' totals">
                    {formatCurrency(counted.reduce((sum, i) => sum + (i.total ?? 0), 0))} Total
                  </NeutralPill>
                  <div className={s.spacer} />
                  <Button
                    appearance="subtle"
                    className={toolbarBtn.subtle}
                    icon={
                      expandedGroups[g.name] ? <ChevronDoubleUpRegular /> : <ChevronDoubleDownRegular />
                    }
                    onClick={() =>
                      setExpandedGroups((prev) => ({ ...prev, [g.name]: !prev[g.name] }))
                    }
                  >
                    {expandedGroups[g.name] ? 'Collapse All' : 'Expand All'}
                  </Button>
                  {config.approveDisabled && config.approveDisabledReason && (
                    <Caption1>{config.approveDisabledReason}</Caption1>
                  )}
                  {config.exportOnly ? (
                    // YEAR, PERIOD AND EXPORT ARE ONE UNIT (QA 2026-10-04,
                    // C2-01): they wrap to the next line together or not at
                    // all. On a short company name the year stayed up on the
                    // header line while the period and Export dropped below.
                    <div className={s.exportUnit}>
                      {config.pickFiscalPeriod && (
                        // THE FISCAL YEAR (owner, 2026-09-23, D-138): "They should
                        // probably pick the fiscal year, as well. However, I would
                        // feel safe defaulting this dropdown to whatever is the
                        // current year." So it never blocks Export; it is there to
                        // change when the period belongs to another year.
                        <Dropdown
                          className={s.fiscalYear}
                          button={{ className: s.fiscalButton }}
                          aria-label={`Fiscal year for ${g.name}`}
                          disabled={submitting}
                          selectedOptions={[String(groupYear)]}
                          value={`Fiscal Year ${groupYear}`}
                          onOptionSelect={(_, d) => {
                            const n = Number(d.optionValue)
                            if (Number.isInteger(n)) setFiscalYearByGroup((prev) => ({ ...prev, [g.name]: n }))
                          }}
                        >
                          {fiscalYearChoices(currentYear).map((y) => (
                            <Option key={y} value={String(y)}>
                              {String(y)}
                            </Option>
                          ))}
                        </Dropdown>
                      )}
                      {config.pickFiscalPeriod && (
                        // THE FISCAL PERIOD, picked by the controller (owner,
                        // 2026-09-23, D-137): "we cannot assume the financial
                        // period that goes into the CSV export." Options 1-12, no
                        // default, one per company group; Export stays disabled
                        // until a number is picked.
                        <Dropdown
                          className={s.fiscalPeriod}
                          button={{ className: s.fiscalButton }}
                          aria-label={`Fiscal period for ${g.name}`}
                          placeholder="Fiscal Period"
                          disabled={submitting}
                          selectedOptions={groupPeriod === undefined ? [] : [String(groupPeriod)]}
                          value={groupPeriod === undefined ? '' : `Fiscal Period ${groupPeriod}`}
                          onOptionSelect={(_, d) => {
                            const n = Number(d.optionValue)
                            if (Number.isInteger(n) && n >= 1 && n <= 12) {
                              setFiscalPeriodByGroup((prev) => ({ ...prev, [g.name]: n }))
                            }
                          }}
                        >
                          {FISCAL_PERIODS.map((n) => (
                            <Option key={n} value={String(n)}>
                              {String(n)}
                            </Option>
                          ))}
                        </Dropdown>
                      )}
                      {/* ONE GREEN BUTTON, no "Selected" suffix — it acts on the
                          WHOLE group (`selectableIds`: decidable and not on
                          hold — the same guard `resolveDecisionBatch` applies a
                          second time inside `submit`), never a hand-picked
                          subset. No client-side uncoded pre-check here — see
                          `openBulkApprove`'s own comment on why. */}
                      <Button
                        // Medium (the Fluent default), matching "Approve
                        // Selected" on the earlier approval screens — owner,
                        // 2026-08-28. Every other button on this line stays
                        // small; this is the one that acts.
                        className={btn.approve}
                        disabled={selectableIds.length === 0 || submitting || config.approveDisabled || needsPeriod}
                        title={exportWhy}
                        // NAMED BY ITS LABEL, THEN ITS REASON (QA 2026-09-30,
                        // C2-03; D-157): a disabled Export reads "Export — Pick a
                        // fiscal period first.", as every disabled button's
                        // non-hold reason stays in its name.
                        {...actionA11y(config.approveLabel, { reason: exportWhy })}
                        onClick={() => void exportGroupNow(selectableIds, g.name, groupPick, groupHeld)}
                      >
                        {config.approveLabel}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Button
                        size="small"
                        className={btn.deny}
                        icon={<DismissRegular />}
                        disabled={groupSelected.length === 0 || submitting}
                        onClick={() => openDeny(groupSelected, DECISION_SOURCE.bulk_queue)}
                      >
                        {groupSelected.length === 0 ? 'Deny Selected' : `Deny ${groupSelected.length} Selected`}
                      </Button>
                      <Button
                        size="small"
                        className={btn.approve}
                        icon={<CheckmarkRegular />}
                        disabled={
                          groupSelected.length === 0 || submitting || config.approveDisabled || groupUncoded > 0
                        }
                        title={
                          groupUncoded > 0
                            ? 'A selected invoice in this company still has a line item without a full code. Finish its coding, or clear it from the selection, before approving.'
                            : config.approveDisabled
                              ? config.approveDisabledReason
                              : undefined
                        }
                        onClick={() => openBulkApprove(groupSelected)}
                      >
                        {groupSelected.length === 0
                          ? `${config.approveLabel} Selected`
                          : `${config.approveLabel} ${groupSelected.length} Selected`}
                      </Button>
                    </>
                  )}
                </div>
                  {/* No summary box under a group and no grand total
                      (owner, 2026-08-28: "drop the summary box under each
                      group") — the count and total are the header pills. The
                      switch for them, never turned on after that, was deleted
                      in round 2 wave C5 (D-289). */}
                  {renderQueue(g.invoices, {
                    allSelected: groupAllSelected,
                    onToggleAll: () =>
                      sel.setMany(groupAllSelected ? groupIds : selectableIds, !groupAllSelected),
                    expandAll: !!expandedGroups[g.name],
                  })}
                </section>
              </Fragment>
            )
          })}
        </div>
      ) : (
        renderQueue(filtered, {
          allSelected: sel.allSelected,
          onToggleAll: sel.toggleAll,
          /* INLINE WITH EXPAND ALL, not a bar of their own (owner, 2026-09-15:
             "move those buttons to be inline with and to the right of Expand
             all"). They sat in an action bar above this queue whose left half
             was empty by design — the count had moved into the button on
             2026-09-14 — so the screen opened with one row of white space
             above another row carrying Select all. Export Control and Closing
             Confirmation had already built this shape per company group; this
             is the ungrouped screens catching up. */
          toolbarActions: (
            <>
              {config.approveDisabled && config.approveDisabledReason && (
                <Caption1>{config.approveDisabledReason}</Caption1>
              )}
              {bulkUncodedReason && <Caption1 className={s.uncoded}>{bulkUncodedReason}</Caption1>}
              <Button
                className={btn.deny}
                icon={<DismissRegular />}
                disabled={sel.count === 0 || submitting}
                onClick={() => openDeny(selectedIds, DECISION_SOURCE.bulk_queue)}
              >
                {sel.count === 0 ? 'Deny Selected' : `Deny ${sel.count} Selected`}
              </Button>
              <Button
                className={btn.approve}
                icon={<CheckmarkRegular />}
                disabled={sel.count === 0 || submitting || config.approveDisabled || selectedUncoded > 0}
                title={bulkUncodedReason ?? (config.approveDisabled ? config.approveDisabledReason : undefined)}
                onClick={() => openBulkApprove(selectedIds)}
              >
                {sel.count === 0 ? `${config.approveLabel} Selected` : `${config.approveLabel} ${sel.count} Selected`}
              </Button>
            </>
          ),
        })
      )}

      <InvoiceDetailModal
        open={!!openInvoice}
        // The LIVE row, not the click-time snapshot (item 9 review fix) — the
        // modal's own body (header panel, the subtotal/tax/total fed to the
        // tables) was still reading the stale `openInvoice` even after
        // `openInvoiceLive` was introduced for the action bar, because only
        // the action bar had been switched over. Safe to swap: the modal's
        // effects and `EditableInvoiceForm`'s `key` are all on `invoice?.id`
        // (unchanged between the two), so this re-renders, it does not
        // remount — no scroll loss, no PDF refetch, no lost edits (B5).
        invoice={openInvoiceLive}
        showPdf={config.showPdf}
        user={user}
        screen={config.step}
        // A HELD INVOICE'S CODING STAYS EDITABLE (owner, 2026-10-03 — D-282:
        // "Holds just prevent invoices from moving to the next step. Editable
        // fields remain editable."). Only the decisions are blocked.
        editAccounts={config.editAccounts}
        editDepartments={config.editDepartments}
        // Wherever the server may grant them (`lib/required.ts`, D-192).
        editAdditionalDetail={categoryPickableAt(config.step)}
        // The hold answer the modal's action bar reads (D2-01).
        // The modal's lines, so Approve can name an Inactive Account (D-175).
        onLineItemsChange={onModalLines}
        // A GL edit in the modal must reach the card behind it: refresh the row
        // and invalidate its cached line items. Without this the card kept
        // showing the pre-edit coding until a full Refresh, which also
        // collapses every expanded card.
        onChanged={() => {
          setLineItemsNonce((n) => n + 1)
          void reload({ silent: true })
        }}
        onClose={() => setOpenInvoice(null)}
        actions={
          // EXPORT CONTROL'S MODAL: [HOLD] | [TYPED INTO POINT CLICK
          // CARE] (owner, 2026-10-07 — D-311: "add the green Manually Entered
          // Into Point Click Care button from the Manual Entry invoice modal to
          // the Export Control modal, to the right of the Hold button as well
          // as the thin vertical divider line that we usually use there").
          // Move To Manual Entry, which stood left of Hold from 2026-08-31,
          // is gone with the Manual Entry step. Approve and Deny stay absent
          // (owner item 7, 2026-08-19 — the group Export button above is the
          // approve). A held invoice shows Release Hold in the same slot, and
          // the green button is disabled until it is released, or while a line
          // is missing its coding (see `openManualEntry`).
          config.exportOnly
            ? openInvoice &&
              openInvoiceLive &&
              (() => {
                const decidable = canDecide(session, config.step, openInvoiceLive)
                const hold = holdFor(openInvoiceLive)
                const held = !!hold
                const heldTitle = hold ? heldReason(hold) : undefined
                // EVERY CODING CAUSE AT ONCE (QA C1-01), the same builder as the
                // tiers' Approve, with the Inactive Account lines this step can
                // still change (D-308).
                const uncoded = openInvoiceLive.uncoded_line_item_count ?? 0
                const noMarketing = openInvoiceLive.missing_marketing_category_count ?? 0
                const codingReason = codingGateReason(
                  { missing: uncoded, capital: 0, marketing: noMarketing },
                  (glOnly) =>
                    glOnly
                      ? 'Finish the GL coding before confirming entry.'
                      : 'Finish the coding before confirming entry.',
                  strandedReasons,
                )
                const otherReason = codingReason ?? (decidable ? undefined : notAuthorizedReason)
                return (
                  <>
                    {holdControl(openInvoiceLive, decidable, 'button')}
                    <ActionDivider />
                    {/* No icon on a labelled modal button (owner, 2026-09-30, D-245). */}
                    <Button
                      className={btn.approve}
                      disabled={submitting || !decidable || held || codingReason !== undefined}
                      title={
                        heldTitle ??
                        otherReason ??
                        'Confirm this invoice was entered into Point Click Care'
                      }
                      {...actionA11y('Typed Into Point Click Care', {
                        held: heldTitle,
                        reason: otherReason,
                      })}
                      onClick={() => openManualEntry(openInvoiceLive.id)}
                    >
                      Typed Into Point Click Care
                    </Button>
                  </>
                )
              })()
            : openInvoice &&
              openInvoiceLive &&
          (() => {
            const decidable = canDecide(session, config.step, openInvoiceLive)
            const hold = holdFor(openInvoiceLive)
            const held = !!hold
            const heldTitle = hold ? heldReason(hold) : undefined
            const modalApproveReason =
              uncodedReason(openInvoiceLive, 'modal', strandedReasons) ??
              (!decidable ? notAuthorizedReason : config.approveDisabled ? config.approveDisabledReason : undefined)
            const modalApproveTitle = heldTitle ?? modalApproveReason
            // [HOLD] [DENY] | [APPROVE] (owner, 2026-09-29: "The Hold button
            // should always sit to the left of a small vertical divider to the
            // left of the Approve button, like it already does at AP/AR
            // Approval"). The Import Control editor reads [Hold] [Archive] | [Release];
            // the tiers have no Archive, so Deny takes its place and Hold stays
            // immediately left of the negative action (D-157).
            return (
              <>
                {holdControl(openInvoiceLive, decidable, 'button')}
                {/* NO ICONS ON THE MODAL'S LABELLED BUTTONS (owner, 2026-09-30,
                    D-245). The queue row's icon-only Deny and Approve keep theirs. */}
                <Button
                  className={btn.deny}
                  disabled={submitting || !decidable || held}
                  title={heldTitle ?? (decidable ? undefined : notAuthorizedReason)}
                  {...actionA11y('Deny', { held: heldTitle, reason: decidable ? undefined : notAuthorizedReason })}
                  onClick={() => openDeny([openInvoiceLive.id], DECISION_SOURCE.modal)}
                >
                  Deny
                </Button>
                <ActionDivider />
                <Button
                  className={btn.approve}
                  disabled={
                    submitting ||
                    config.approveDisabled ||
                    !decidable ||
                    held ||
                    uncodedCount(openInvoiceLive) > 0
                  }
                  title={modalApproveTitle}
                  // THE REASON REACHES THE ACCESSIBLE NAME, as it does on the
                  // editor's Release (QA C1-01): a `title` alone is a
                  // description, which not every screen reader announces. A
                  // HOLD is the description instead, so a held invoice's button
                  // is still named "Approve" (QA C2-04, `actionA11y`).
                  {...actionA11y(config.approveLabel, { held: heldTitle, reason: modalApproveReason })}
                  onClick={() => approveNow([openInvoiceLive.id], DECISION_SOURCE.modal)}
                >
                  {config.approveLabel}
                </Button>
              </>
            )
          })()
        }
      />

      <DecisionDialog
        open={!!pending}
        /* ONE HEADER FOR EVERY IRREVERSIBLE CONFIRMATION (owner, 2026-09-14):
           "Are you sure?", on Deny single and bulk, on Typed Into
           Point Click Care, and on Not An Invoice. Each dialog's own body says what is about to
           happen; the header asks the question. */
        title="Are you sure?"
        body={
          !dialogContent
            ? undefined
            : dialogContent.kind === 'manualEntry'
              // The Manual Entry step's own confirmation, verbatim, which moved
              // here with its button (D-311). Move To Manual Entry's sentence
              // described a step that no longer exists.
              ? 'You are confirming that this invoice has been manually entered into Point Click Care, meaning that it is on its way to payment. This will remove this invoice from the pipeline here. You cannot undo this action.'
              : dialogContent.kind === 'approve'
                // Export Control never opens this for its Export (owner,
                // 2026-09-23, D-138) — `exportGroupNow` sends it directly.
                ? bulkApproveBody(dialogContent.ids.length)
                : dialogContent.bulk
                  // NO "YOU CANNOT UNDO THIS ACTION" (QA C2-01): since D-158 a
                  // denied invoice can be reinstated from Denial Review. And no
                  // "this comment" (QA C2-02): the Reason is optional unless the
                  // Category is Other, so there may be no comment at all.
                  ? bulkDenyBody(dialogContent.ids.length)
                  : DENY_BODY
        }
        requireReason={dialogContent?.kind === 'deny' || dialogContent?.kind === 'manualEntry'}
        /* One plain label on both (owner, 2026-09-14) — the field is in a
           dialog that has already said which action is being taken, so naming
           the action again in its label reads as a second question. The
           EXAMPLE moves into the placeholder, which is what Not An Invoice
           already did. The label was "Reason" until 2026-10-08, when the owner
           renamed it "Comment" in every pop-up (D-326). */
        reasonLabel="Comment"
        reasonPlaceholder={
          dialogContent?.kind === 'manualEntry'
            ? "The 1099 Amount on one of this invoice's line items exceeded the invoice's Total, blocking import into PCC."
            : 'These purchases were not authorized.'
        }
        /* A CATEGORY EACH, AND THEY ARE NOT THE SAME LIST (owner, 2026-09-15).
           Deny takes the four denial reasons; Typed Into Point Click
           Care takes **PCC Import Error** and **Other** (D-315), because none of
           the denial reasons describes a bad fit for the CSV. Both are REQUIRED, and each
           makes the free-text Reason optional unless the pick is Other. */
        categories={
          dialogContent?.kind === 'deny'
            ? DENY_CATEGORIES
            : dialogContent?.kind === 'manualEntry'
              ? MANUAL_ENTRY_CATEGORIES
              : undefined
        }
        confirmLabel={
          dialogContent?.kind === 'approve'
            ? config.approveLabel
            : dialogContent?.kind === 'manualEntry'
              ? 'Typed Into Point Click Care'
              : 'Deny'
        }
        confirmClassName={
          dialogContent?.kind === 'approve' || dialogContent?.kind === 'manualEntry' ? btn.approve : undefined
        }
        /* Not destructive: the invoice is being completed, not refused. */
        destructive={dialogContent?.kind === 'deny'}
        submitting={submitting}
        onCancel={() => setPending(null)}
        onConfirm={(reason, category) =>
          pending &&
          submit(
            pending.ids,
            pending.kind === 'approve'
              ? DecisionOutcome.Approved
              : pending.kind === 'manualEntry'
                ? DecisionOutcome.RequiresManualEntry
                : DecisionOutcome.Denied,
            reason,
            category,
            undefined,
            pending.source,
          )
        }
      />
    </div>
  )
}
